import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../api/users';
import toast from 'react-hot-toast';
import { ROLES } from '../../utils/permissions';

const userSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres').optional().or(z.literal('')),
  confirmPassword: z.string().optional().or(z.literal('')),
  role: z.enum(['admin', 'despachador', 'visor_reportes'], {
    errorMap: () => ({ message: 'Selecciona un rol válido' })
  }),
  nombre: z.string().min(1, 'El nombre es requerido'),
  activo: z.boolean(),
}).refine((data) => {
  // Only validate password match if password is provided
  if (data.password && data.password.length > 0) {
    return data.password === data.confirmPassword;
  }
  return true;
}, {
  message: "Las contraseñas no coinciden",
  path: ["confirmPassword"],
});

const UserForm = ({ user, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const isEditing = !!user;
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: {
      email: user?.email || '',
      password: '',
      confirmPassword: '',
      role: user?.role || 'despachador',
      nombre: user?.nombre || '',
      activo: user?.activo === 1 || user?.activo === true || true,
    },
  });

  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario creado exitosamente');
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Error al crear usuario');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario actualizado exitosamente');
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Error al actualizar usuario');
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Remove password if empty when editing
      if (isEditing && !data.password) {
        delete data.password;
      }
      
      // Remove confirmPassword as it's only for frontend validation
      delete data.confirmPassword;
      
      if (user) {
        await updateMutation.mutateAsync({ id: user.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{user ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
          <button className="modal-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="modal-body">
            {/* Row 1: Nombre - Correo */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nombre">Nombre *</label>
                <input
                  id="nombre"
                  type="text"
                  {...register('nombre')}
                  className={errors.nombre ? 'error' : ''}
                />
                {errors.nombre && (
                  <span className="error-message">{errors.nombre.message}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Correo electrónico *</label>
                <input
                  id="email"
                  type="email"
                  {...register('email')}
                  className={errors.email ? 'error' : ''}
                  disabled={isEditing} // Don't allow email changes when editing
                />
                {errors.email && (
                  <span className="error-message">{errors.email.message}</span>
                )}
                {isEditing && (
                  <small style={{ opacity: 0.7 }}>El email no se puede modificar</small>
                )}
              </div>
            </div>

            {/* Row 2: Contraseña - Confirmar contraseña */}
            <div className="form-row" style={{ marginTop: '20px' }}>
              <div className="form-group">
                <label htmlFor="password">
                  Contraseña {isEditing ? '(dejar vacío para mantener actual)' : '*'}
                </label>
                <input
                  id="password"
                  type="password"
                  {...register('password')}
                  className={errors.password ? 'error' : ''}
                  placeholder={isEditing ? 'Nueva contraseña (opcional)' : 'Contraseña'}
                />
                {errors.password && (
                  <span className="error-message">{errors.password.message}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">
                  Confirmar Contraseña {isEditing ? '(opcional)' : '*'}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  {...register('confirmPassword')}
                  className={errors.confirmPassword ? 'error' : ''}
                  placeholder={isEditing ? 'Confirmar nueva contraseña' : 'Confirmar contraseña'}
                />
                {errors.confirmPassword && (
                  <span className="error-message">{errors.confirmPassword.message}</span>
                )}
              </div>
            </div>

            {/* Row 3: Rol - Status */}
            <div className="form-row" style={{ marginTop: '20px', marginBottom: '12px' }}>
              <div className="form-group">
                <label htmlFor="role">Rol *</label>
                <select
                  id="role"
                  {...register('role')}
                  className={errors.role ? 'error' : ''}
                >
                  <option value="despachador">Despachador</option>
                  <option value="admin">Administrador</option>
                  <option value="visor_reportes">Visor de Reportes</option>
                </select>
                {errors.role && (
                  <span className="error-message">{errors.role.message}</span>
                )}
              </div>

              <div className="form-group" style={{ alignItems: 'center', marginTop: 'auto', marginBottom: '12px' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    {...register('activo')}
                  />
                  <span className="checkmark"></span>
                  Activo
                </label>
              </div>
            </div>

          <div className="modal-footer">
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Guardando...' : (user ? 'Actualizar' : 'Crear')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;
